// packages import
import { yupResolver } from "@hookform/resolvers/yup";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Collapse, Grid, IconButton, Typography } from "@mui/material";
import phone from "phone";
import { FC, useContext, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
// component import
import { Alert } from "../common/Alert";
import { CustomController } from "../common/CustomController";
import CustomPhoneController from "../common/CustomPhoneController";
// others 
import { UpdateUserInput, useUpdateUserMutation } from "../../../generated";
import { CONFLICT_EXCEPTION, NOT_APPLICABLE, SOMETHING_WENT_WRONG } from "../../constants";
import { AuthContext } from "../../context";
import { PersonalSettingProps } from "../../interfaceTypes";
import { CardBox, PersonalSettingFormBox } from "../../theme/styleComponents";
import { caseDetailGrid } from "../../theme/styleConstants";
import { capitalizeCase, titleize } from "../../utils";
import { updateUserValidationSchema } from "../../validationSchema";

export const PersonalSetting: FC<PersonalSettingProps> = ({ currentUser }) => {
  const [isEdit, setIsEdit] = useState<boolean>()
  const { setUser } = useContext(AuthContext)
  const { firstName, lastName, phoneNumber, id: userId } = currentUser || {}
  const userFullName = firstName ? `${capitalizeCase(firstName)} ${capitalizeCase(lastName || '') || ''}` : NOT_APPLICABLE

  const methods = useForm<UpdateUserInput>({
    mode: "all",
    resolver: yupResolver(updateUserValidationSchema),

    defaultValues: {
      firstName: titleize(firstName || ''),
      lastName: titleize(lastName || ''),
      phoneNumber: phoneNumber
    }
  });

  const [updateUserMutation, { loading }] = useUpdateUserMutation({
    onError(error) {
      if (error.message === CONFLICT_EXCEPTION) Alert.error(CONFLICT_EXCEPTION)
      else Alert.error(SOMETHING_WENT_WRONG)
    },

    onCompleted(data) {
      if (data) {
        const { updateUser } = data

        if (updateUser) {
          const { response, user } = updateUser
          const { status } = response || {}

          if (status === 200 && user) {
            Alert.success('Personal settings updated successfully')
            setUser(user)
            setIsEdit(!isEdit)
          }
        }
      }
    }
  })

  const { handleSubmit } = methods;

  const onSubmit: SubmitHandler<UpdateUserInput> = async (data: UpdateUserInput) => {
    const { firstName, lastName, phoneNumber } = data
    const validatePhoneNumber = phone(phoneNumber!, { country: 'USA' })
    const { isValid, phoneNumber: validPhoneNumber } = validatePhoneNumber

    phoneNumber ?
      isValid ?
        await updateUserMutation({
          variables: {
            updateUserInput: {
              id: userId || '',
              firstName: titleize(firstName?.trim() || ''),
              lastName: titleize(lastName?.trim() || ''),
              phoneNumber: validPhoneNumber
            }
          }
        })
        :
        Alert.error('Phone number not valid')
      :
      await updateUserMutation({
        variables: {
          updateUserInput: {
            id: userId || '',
            firstName: titleize(firstName?.trim() || ''),
            lastName: titleize(lastName?.trim() || ''),
            phoneNumber: ""
          }
        }
      })
  }

  const handleEdit = () => setIsEdit(!isEdit)

  return (
    <CardBox>
      <FormProvider {...methods}>
        <form>
          <PersonalSettingFormBox padding={3}>
            <Typography variant="h6" fontSize="18px">Personal Settings</Typography>

            {isEdit ?
              <Box>
                <IconButton color="primary" disabled={loading} onClick={handleEdit} aria-label='back'>
                  <ArrowBackIcon />
                </IconButton>

                <IconButton color="primary" disabled={loading} onClick={handleSubmit(onSubmit)} aria-label='save'>
                  <SaveIcon />
                </IconButton>
              </Box>
              :
              <IconButton color="primary" onClick={handleEdit} aria-label='edit'>
                <EditIcon />
              </IconButton>
            }
          </PersonalSettingFormBox>

          <Collapse in={!isEdit}>
            <Grid container sx={caseDetailGrid}>
              <Grid item md={6} sm={6} xs={12}>
                <Typography variant="h6" className="CaseDetail-Title">Name</Typography>
              </Grid>

              <Grid item md={6} sm={6} xs={12}>
                <Typography variant="h6" className="CaseDetail-SubTitle">{userFullName}</Typography>
              </Grid>
            </Grid>

            <Grid container sx={caseDetailGrid}>
              <Grid item md={6} sm={6} xs={12}>
                <Typography variant="h6" className="CaseDetail-Title">Phone number</Typography>
              </Grid>

              <Grid item md={6} sm={6} xs={12}>
                <Typography variant="h6" className="CaseDetail-SubTitle">
                  {phoneNumber || NOT_APPLICABLE}
                </Typography>
              </Grid>
            </Grid>
          </Collapse>

          <Collapse in={isEdit}>
            <Box padding={3}>
              <Grid container spacing='10px'>
                <Grid item md={6} sm={6} xs={12}>
                  <CustomController
                    controllerName="firstName"
                    controllerLabel="First Name"
                    fieldType="text"
                  />
                </Grid>

                <Grid item md={6} sm={6} xs={12}>
                  <CustomController
                    controllerName="lastName"
                    controllerLabel="Last Name"
                    fieldType="text"
                  />
                </Grid>
              </Grid>

              <CustomPhoneController
                controllerName='phoneNumber'
                controllerLabel='Phone Number'
              />
            </Box>
          </Collapse>
        </form>
      </FormProvider>
    </CardBox>
  )
}